import { Injectable, inject } from '@angular/core';

import { GroupItemType } from '@cds/betting-offer';
import { overrideActiveItemByOrder, sortByKey } from '@frontend/sports/common/core/utils/list';
import { CustomerDnaProfileStorageService } from '@frontend/sports/customer-dna/data-access';
import { WidgetContext } from '@frontend/sports/types/components/widget';
import { TabbedGridNavigationContext, TabbedGridPayload, WidgetNavigation } from '@frontend/sports/types/components/widget/types';
import { WidgetTabBarDisplay, WidgetTabBarItem, WidgetTabBarMapperService } from '@frontend/sports/widget/feature/tab-bar';
import { sum, toNumber } from 'lodash-es';

import { DisplayOptions } from './tabbed-grid.model';

export interface TabbedGridTabBarItem extends WidgetTabBarItem {
    group?: {
        count?: number;
        type: GroupItemType;
        name?: string;
    };
}

@Injectable({ providedIn: 'root' })
export class TabbedGridNavigationService extends WidgetTabBarMapperService {
    private readonly customerDnaProfileStorageService = inject(CustomerDnaProfileStorageService);

    getCount(navigation: WidgetNavigation[], navigationContext?: TabbedGridNavigationContext): number {
        return sum(navigation.map((n) => n.count ?? 0));
    }

    map(widgetId: string, navigation?: WidgetNavigation[], navigationContext?: TabbedGridNavigationContext): TabbedGridTabBarItem[] {
        if (!navigation?.length) {
            return [];
        }

        return navigation.map((item) => this.mapItem(item, widgetId, navigationContext));
    }

    get(widgetId: string, payload: TabbedGridPayload): TabbedGridTabBarItem[][] {
        const navigationContext: TabbedGridNavigationContext = {
            competition: payload.competitionNavigationDisplay,
            sport: payload.sportsNavigationDisplay,
        };

        let primary = this.map(widgetId, payload.navigation ?? [], navigationContext);
        let secondary = this.map(widgetId, payload.secondaryNavigation ?? [], navigationContext);

        primary.forEach((item) => this.mapActive(item, payload.primaryActiveNavigation, payload));
        secondary.forEach((item) => this.mapActive(item, payload.secondaryActiveNavigation, payload));

        primary = this.setCustomOrderForSports(primary);
        secondary = this.setCustomOrderForSports(secondary);

        if (primary.length && !primary.find((item) => item.active)) {
            primary[0].active = true;
        }

        if (secondary.length && !secondary.find((item) => item.active)) {
            secondary[0].active = true;
        }

        return [primary, secondary];
    }

    private setCorrectSportIdsFromContext(tabbedGrid: TabbedGridTabBarItem[]) {
        tabbedGrid.forEach((tab) => (tab.sportId = tab.sportId ?? tab.context.sportId));
    }

    private getNavigationDsiplay(navigationContext?: TabbedGridNavigationContext):
        | {
              [key: string]: string;
          }
        | undefined {
        if (!navigationContext) {
            return;
        }

        return {
            [GroupItemType.Competition]: navigationContext.competition!,
            [GroupItemType.Sport]: navigationContext.sport!,
        };
    }

    private mapItem(item: WidgetNavigation, widgetId: string, navigationContext?: TabbedGridNavigationContext): TabbedGridTabBarItem {
        const { title, icon, display } = this.getView(item, this.getNavigationDsiplay(navigationContext));

        return {
            context: this.getContext(item),
            display,
            title,
            id: this.getId(widgetId, item),
            itemId: this.getIdentifier(item),
            widgetId,
            icon,
            group: {
                count: item.count,
                type: item.type,
                name: item.name,
            },
            sportId: item.sport?.id,
        };
    }

    private getIdentifier(item: WidgetNavigation): number | string {
        switch (item.type) {
            case GroupItemType.Competition:
            case GroupItemType.Sport:
                return toNumber(item.id);
            default:
                return item.id;
        }
    }

    private mapActive(item: TabbedGridTabBarItem, activeId: string | undefined, payload: TabbedGridPayload): void {
        const { competition, sport, stage } = payload.fixtures[0];
        const active = activeId ? item.itemId.toString() === activeId : undefined;

        // for match list module with grouping we get the active item per navigation on the payload
        // while for existing implementations, like the lobby live/lobby upcomming/sport lobby
        // we still have to decide the active item depending on the fixture information

        if (active !== undefined) {
            item.active = active;
        } else {
            switch (item.group?.type) {
                case GroupItemType.Competition:
                    item.active = competition.id === item.itemId || competition.parentLeagueId === item.itemId;
                    break;
                case GroupItemType.FixtureState:
                    item.active = stage === item.id;
                    break;
                default:
                    item.active = sport.id === item.itemId;
                    break;
            }
        }
    }

    private getView(
        item: WidgetNavigation,
        navigationDisplay?: { [key: string]: string },
    ): { title?: string; icon: string; display: WidgetTabBarDisplay } {
        let display = WidgetTabBarDisplay.Default;
        let title: string | undefined = '';

        const config = navigationDisplay?.[item.type];
        let icon: string[] = [];

        switch (item.type) {
            case GroupItemType.Competition:
                icon.push(`country-icon`);
                icon.push(`c${item.region?.id}`);
                break;
            case GroupItemType.Sport:
                icon.push(`sports-${item.id}`);
                break;
        }

        if (config !== DisplayOptions.IconOnly) {
            title = item.name;
        }

        switch (config) {
            case DisplayOptions.NameWithLogo:
                display = WidgetTabBarDisplay.Logo;
                break;
            case DisplayOptions.IconOnly:
                display = WidgetTabBarDisplay.Icon;
                icon.push('icon-only');
                break;
            case DisplayOptions.NameWithIcon:
            case DisplayOptions.NameWithFlag:
                display = WidgetTabBarDisplay.Icon;
                break;
            case DisplayOptions.NameOnly:
                icon = [];
                break;
            default:
                icon.slice(0);
                break;
        }

        return { title, icon: icon.join(' '), display };
    }

    private getContext(item: WidgetNavigation): Partial<WidgetContext> {
        switch (item.type) {
            case GroupItemType.Competition:
                return { competitionId: toNumber(item.id), sportId: item.sport?.id };
            case GroupItemType.FixtureState:
                return { fixtureState: item.id };
            case GroupItemType.Sport:
                return { sportId: toNumber(item.id) };
            default:
                return {};
        }
    }

    private setCustomOrderForSports(sports: TabbedGridTabBarItem[]): TabbedGridTabBarItem[] {
        const topSports = this.customerDnaProfileStorageService.customerDnaTopSports;

        if (!!topSports?.length && sports.length) {
            this.setCorrectSportIdsFromContext(sports);
            const sortedItems = sortByKey(topSports, sports, 'sportId');
            return overrideActiveItemByOrder(sortedItems);
        } else {
            return sports;
        }
    }
}
